import { Component, Host, Prop, h } from '@stencil/core';

import { HierarchicalData, ReplacementVehicleOptions, RvLookingForData } from '../../../interfaces/interface.lookup';
import { ANSWER_LEAD_TYPE_OPT_IN } from '../../../utils/utils.answers';
import { getReplacementVehicleMakeModels } from '../../../utils/utils.contact';
import { QUESTION_CERTIFIED_CARS, QUESTION_LEAD_TYPE, QUESTION_MAKE_MODEL, QUESTION_NEW_CARS, QUESTION_USED_CARS } from '../../../utils/utils.question';
@Component({
  tag: 'contact-replacement-vehicle',
  styleUrl: 'contact-replacement-vehicle.css',
})
export class ContactReplacementVehicle {
  @Prop() showFormValidation: boolean;
  @Prop() formErrors: Set<string>;
  @Prop() isLookingForReplacementVehicle: boolean;
  @Prop() isLookingForNewCars: boolean;
  @Prop() isLookingForCertifiedCars: boolean;
  @Prop() isLookingForUsedCars: boolean;
  @Prop() minYear: number;
  @Prop() maxYear: number;
  @Prop() makeModelId: string;
  @Prop() makeId: string;
  @Prop() yearRangeMin: number;
  @Prop() yearRangeMax: number;
  @Prop() makesModels: HierarchicalData[];
  @Prop() rvLookingForOptions: RvLookingForData[];
  @Prop() replacementVehicleOptions: ReplacementVehicleOptions;
  @Prop() showReplacementVehicleSection: boolean = true;
  @Prop() answerValueForLeadTypeNo: string;

  render() {
    return (
      <Host>
        {this.showReplacementVehicleSection && this.renderLeadType()}
        {this.showReplacementVehicleSection && this.renderReplacementQuestion()}
      </Host>
    );
  }

  renderReplacementQuestion() {
    if (this.isLookingForReplacementVehicle === false) {
      return this.renderOptOut();
    } else if (this.isLookingForReplacementVehicle === true) {
      return this.renderReplacementVehicle();
    }
  }

  renderLeadType() {
    return (
      <div>
        <div class="text-lg font-bold">Looking to Replace This Car?</div>
        <div class="text-sm mb-4">Let us connect you with the right participating dealers based on your next car! Your Offer will be the same either way.</div>
        <question-enum
          questionId={QUESTION_LEAD_TYPE}
          optionAnswers={this.getLeadTypeOptions()}
          isDirty={this.showFormValidation}
          errorMessage="Please tell us if you plan to replace your car."
          hasError={this.formErrors.has('qrv/LeadType')}
        />
      </div>
    );
  }

  renderReplacementVehicle() {
    const hasReplacementVehicleError = this.formErrors.has('qrv/YearRange') || this.formErrors.has('qrv/MakeModel1');
    return (
      <div class="mb-4">
        <div class="text-lg font-bold mt-5">What are you interested in next?</div>
        <div class="text-sm mb-4">Your Offer will be the same either way.</div>

        <div class="flex flex-col gap-2">
          <question-multiselect
            questionId={QUESTION_LEAD_TYPE}
            options={this.rvLookingForOptions}
            optionAnswers={this.getLookingForValues()}
            errorMessage="Please select at least one option."
            hasError={this.formErrors.has('qrv/LookingFor')}
            isDirty={this.showFormValidation}
          />
          <question-integer-range
            inputDefaultLabel="Select"
            questionId="qrv/YearRange"
            heading="Year"
            order="descending"
            value={`${this.minYear},${this.maxYear}`}
            rangeMin={this.yearRangeMin}
            rangeMax={this.yearRangeMax}
          />
          {/*  TODO There's no questionId here since there's only one "answer" for both make and model so we log an error when we try to call onQuestionAnswered with no answerKey. This doesn't trigger a problem in the app at all, but we shouldn't log an error if there's no problem */}
          <question-listselect questionId={QUESTION_MAKE_MODEL} heading="Make" options={this.buildMakes()} value={this.makeId} />
          <question-listselect questionId={QUESTION_MAKE_MODEL} heading="Model" options={this.buildModels()} value={this.makeModelId} isDisabled={!this.makeId} />
          {this.showFormValidation && hasReplacementVehicleError && <div class="text-xs pl-4 text-ico-error">Please select a year, make, and model.</div>}
        </div>
      </div>
    );
  }

  renderOptOut() {
    return <div class="my-5 text-lg font-bold">Great! Participating Dealers are ready to buy your car for cash.</div>;
  }

  getLookingForValues() {
    return {
      [QUESTION_NEW_CARS]: { value: this.isLookingForNewCars },
      [QUESTION_CERTIFIED_CARS]: { value: this.isLookingForCertifiedCars },
      [QUESTION_USED_CARS]: { value: this.isLookingForUsedCars },
    };
  }

  getLeadTypeOptions() {
    return [
      {
        label: 'Yes',
        value: ANSWER_LEAD_TYPE_OPT_IN,
        selected: this.isLookingForReplacementVehicle === true,
      },
      {
        label: 'No',
        value: this.answerValueForLeadTypeNo,
        selected: this.isLookingForReplacementVehicle === false,
      },
    ];
  }

  buildMakes() {
    return getReplacementVehicleMakeModels(this.makesModels, this.replacementVehicleOptions).map((mm) => ({
      value: mm.id,
      label: mm.label,
    }));
  }

  buildModels() {
    const { makeId } = this;
    const models = getReplacementVehicleMakeModels(this.makesModels, this.replacementVehicleOptions).find((mm) => mm.id === makeId);
    if (models) {
      return models.values.map((m) => ({
        value: m.id,
        label: m.label,
      }));
    }

    return [];
  }
}
